import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import COS from "cos-js-sdk-v5";
import {
  apiRequest,
  showAlert,
  showLoading,
  API_SERVER_URL,
  getTeamUUID,
  getPlayerCode,
  compressImage,
  getSessionUUID,
  TEAM_COLORS,
  uuidv4,
  formatPlayerNo,
  getEnergyHubPeopleNumText,
  replaceUrlParams,
} from "../../utils";
import Alert from "../../components/Alert/Alert";

import ss from "./game.module.css";

export const GamePage = () => {
  const navigate = useNavigate();
  const [teamInfo, setTeamInfo] = useState(null);
  // 展示游戏开始
  const [showGameStart, setShowGameStart] = useState(false);
  // 1 - 游戏开始 or 2 - 游戏结束
  const [gameHintType, setGameHintType] = useState(1);
  // 展示激活成功倒计时
  const [showActiveSuccess, setShowActiveSuccess] = useState(false);
  // 倒计时
  const [startCountdownNum, setStartCountdownNum] = useState(5);
  // 任务列表
  const [taskList, setTaskList] = useState([]);
  // 使用useRef引用相机输入元素
  const cameraInputRef = useRef(null);
  // 拍照类型
  const takePhotoType = useRef("active"); // or 'task'
  // 拍照任务id
  const photoTaskId = useRef(null);
  // 底部任务类型选择，1 - 任务， 2 - 抢夺， 3 - 能量站
  const lastTaskTypeSelected =
    parseInt(new URL(window.location.href).searchParams.get("t"), 10) || 1;
  const [taskTypeSelected, setTaskTypeSelected] =
    useState(lastTaskTypeSelected);
  // 抢夺列表
  const [robList, setRobList] = useState([]);
  // 抢夺日志列表
  const [robLogList, setRobLogList] = useState([]);
  // 抢夺队伍uuid
  const robTeamUUID = useRef(null);
  // 所有队伍抢夺CD倒计时
  const [robCooldownMap, setRobCooldownMap] = useState({});
  // 抢夺倒计时
  const robCountdownRef = useRef(null);
  // 能量站列表
  const [hubList, setHubList] = useState([]);
  // 判定中的能量站
  const [hubJudgingList, setHubJudgingList] = useState([]);
  // 能量站日志列表
  const [hubLogList, setHubLogList] = useState([]);
  // 展示能量站弹层
  const [showHubInfo, setShowHubInfo] = useState(false);
  const [hubInfoClosing, setHubInfoClosing] = useState(false);
  // 当前能量站ref
  const currentHubRef = useRef(null);
  // 队伍中心弹层
  const [showTeamCenter, setShowTeamCenter] = useState(false);
  const [teamCenterClosing, setTeamCenterClosing] = useState(false);
  // 队伍中心信息
  const [teamCenterInfo, setTeamCenterInfo] = useState({});
  // 允许更换任务
  const [allowChangeTask, setAllowChangeTask] = useState(true);
  // 剩余时间倒计时
  const [remainTimeCountdown, setRemainTimeCountdown] = useState(0);
  // 队伍排名
  const [teamRank, setTeamRank] = useState([]);
  // cos 配置
  const cosConfig = useRef();
  // 激活时的能量站状态
  const [activeHubList, setActiveHubList] = useState([]);
  // 判定中的激活能量站
  const [activeHubJudgingList, setActiveHubJudgingList] = useState([]);
  // 激活能量站日志列表
  const [activeHubLogList, setActiveHubLogList] = useState([]);
  // 场次地图信息
  const [sessionMap, setSessionMap] = useState({});
  // 场次补给
  const [showSessionSupply, setShowSessionSupply] = useState(false);
  const [sessionSupply, setSessionSupply] = useState(null);
  const [showSupplyModal, setShowSupplyModal] = useState(false);
  const [supplyModalClosing, setSupplyModalClosing] = useState(false);

  const getSessionMap = async () => {
    apiRequest("/get_session_map", "GET", { sid: getSessionUUID() })
      .then((res) => {
        if (res.code === 0) setSessionMap(res.map);
      })
      .catch((err) => {
        console.error("获取地图失败", err);
        showAlert("网络异常，请刷新重试");
      });
  };

  const getCosConfig = async () => {
    apiRequest("/get_upload_token", "GET", {
      tid: getTeamUUID(),
      code: getPlayerCode(),
    })
      .then((res) => {
        if (res.code === 0) cosConfig.current = res.data;
        else console.error("failed to get upload token");
      })
      .catch((err) => {
        console.error("failed to request get upload token:", err);
        showAlert("网络异常，请刷新重试");
      });
  };

  const getTeamInfo = async (skipLoading) => {
    const loading = skipLoading ? null : showLoading("加载中");
    apiRequest("/get_player_team_info", "GET", {
      tid: getTeamUUID(),
      code: getPlayerCode(),
    })
      .then((res) => {
        if (res.code === 0) {
          setTeamInfo(res.data);
          setRemainTimeCountdown(res.data.remain_time);
          if (res.data.game_status >= 300) {
            getTeamRank();
          }
        } else showAlert(res.msg);
      })
      .finally(() => !skipLoading && loading?.hide());
  };

  const remainTimeCountdownRef = useRef(null);
  useEffect(() => {
    if (remainTimeCountdown >= 1 && teamInfo?.game_status !== 120) {
      remainTimeCountdownRef.current = setTimeout(
        () => setRemainTimeCountdown(remainTimeCountdown - 1),
        1000
      );
    }
    return () => clearTimeout(remainTimeCountdownRef.current);
  }, [remainTimeCountdown, teamInfo?.game_status]);

  // 增加对请求 loading 的保护
  const isLoadingTaskList = useRef(false);
  const getTaskList = async (skipLoading) => {
    if (isLoadingTaskList.current) return;
    isLoadingTaskList.current = true;
    const loading = skipLoading ? null : showLoading("加载中");
    apiRequest("/get_task_list", "POST", {
      tid: getTeamUUID(),
      code: getPlayerCode(),
    })
      .then((res) => {
        if (res.code === 0) {
          setTaskList(res.data.tasks);
          if (res.data.remain_change_time > 0) {
            setAllowChangeTask(false);
            setTimeout(() => {
              setAllowChangeTask(true);
            }, res.data.remain_change_time * 1000);
          }
        } else showAlert(res.msg);
      })
      .finally(() => {
        !skipLoading && loading?.hide();
        isLoadingTaskList.current = false;
      });
  };

  const getRobList = async (skipLoading) => {
    const loading = skipLoading ? null : showLoading("加载中");
    apiRequest("/get_rob_list", "GET", {
      sid: getSessionUUID(),
      tid: getTeamUUID(),
      code: getPlayerCode(),
    })
      .then((res) => {
        if (res.code === 0) {
          setRobList(res.rob_teams);
          const robCooldownMap = {};
          res.rob_teams.forEach((rob) => {
            robCooldownMap[rob.uuid] = rob.cooldown;
          });
          setRobCooldownMap(robCooldownMap);
          setRobLogList(res.player_rob_list);
        } else showAlert(res.msg);
      })
      .finally(() => !skipLoading && loading?.hide());
  };

  const getActiveHubList = async (skipLoading) => {
    const loading = skipLoading ? null : showLoading("加载中");
    apiRequest("/get_active_hub_list", "GET", {
      sid: getSessionUUID(),
      tid: getTeamUUID(),
      code: getPlayerCode(),
    })
      .then((res) => {
        if (res.code === 0) {
          setActiveHubList(res.energy_hubs);
          setActiveHubJudgingList(res.energy_hubs_player_photos);
          setActiveHubLogList(res.team_energy_hub_logs);
        } else showAlert(res.msg);
      })
      .finally(() => !skipLoading && loading?.hide());
  };

  const getHubList = async (skipLoading) => {
    const loading = skipLoading ? null : showLoading("加载中");
    apiRequest("/get_energy_hub_list", "GET", {
      sid: getSessionUUID(),
      tid: getTeamUUID(),
      code: getPlayerCode(),
    })
      .then((res) => {
        if (res.code === 0) {
          setHubList(res.energy_hubs);
          setHubJudgingList(res.energy_hubs_player_photos);
          setHubLogList(res.team_energy_hub_logs);
        } else showAlert(res.msg);
      })
      .finally(() => !skipLoading && loading?.hide());
  };

  const getTeamCenterInfo = async () => {
    // const loading = showLoading("加载中");
    apiRequest("/team_center_info", "GET", {
      tid: getTeamUUID(),
    }).then((res) => {
      if (res.code === 0) setTeamCenterInfo(res.data);
      else showAlert(res.msg);
    });
    // .finally(() => loading.hide());
  };

  const getTeamRank = async (skipLoading) => {
    const loading = skipLoading ? null : showLoading("加载中");
    apiRequest("/get_team_rank", "GET", {
      sid: getSessionUUID(),
    })
      .then((res) => {
        if (res.code === 0) {
          setTeamRank(res.data);
        } else showAlert(res.msg);
      })
      .finally(() => !skipLoading && loading?.hide());
  };

  const getSessionSupply = async () => {
    apiRequest("/get_session_supply", "GET", {
      sid: getSessionUUID(),
      tid: getTeamUUID(),
    }).then((res) => {
      if (res.code === 0) {
        setSessionSupply(res.data);
        if (res.data.status !== 2) localStorage.removeItem("hideSessionSupply");
        // 如果补给状态是已被提交，且在展示补给浮层，更新状态
        if (res.data.status === 1) {
          setHasSubmitSupply(true);
          setSupplyStatusText(
            res.data.photo_team_uuid === getTeamUUID()
              ? "队友已提交照片"
              : "已有其他队伍提交照片"
          );
        } else if (res.data.status === 2) {
          closeSupplyModal();
          setHasTakeSupplyPhoto(false);
        }
        // 补给展示依赖 teamInfo，未激活或者游戏结束后不展示
        setShowSessionSupply(
          res.data &&
            teamInfo?.actived &&
            teamInfo?.game_status < 200 &&
            localStorage.getItem("hideSessionSupply") !== res.data.id.toString()
        );
      } else {
        setShowSessionSupply(false);
        setTimeout(() => setSessionSupply(null), 800);
      }
      // else showAlert(res.msg);
    });
  };

  useEffect(() => {
    if (Object.values(robCooldownMap).every((cooldown) => cooldown <= 0)) {
      clearInterval(robCountdownRef.current);
      return;
    }
    robCountdownRef.current = setTimeout(() => {
      setRobCooldownMap((prev) => {
        const newMap = { ...prev };
        Object.keys(prev).forEach((key) => {
          newMap[key]--;
        });
        return newMap;
      });
    }, 1000);
    return () => clearTimeout(robCountdownRef.current);
  }, [robCooldownMap]);

  useEffect(() => {
    getSessionMap();
    getCosConfig();
    getTeamInfo();
  }, []);

  useEffect(() => {
    if (teamInfo?.game_status === 50 || !teamInfo?.actived) {
      getActiveHubList(true);
    }
  }, [teamInfo?.game_status]);

  const taskTypeSelectedRef = useRef(lastTaskTypeSelected);
  useEffect(() => {
    taskTypeSelectedRef.current = taskTypeSelected;
    if (teamInfo?.game_status >= 100) {
      if (taskTypeSelected === 1) getTaskList(true);
      else if (taskTypeSelected === 2) getRobList(true);
      else if (taskTypeSelected === 3) getHubList(true);
    }
  }, [taskTypeSelected, teamInfo]);

  useEffect(() => {
    if (!teamInfo?.session_uuid) return;

    if (teamInfo?.actived) {
      // 如果激活了，获取任务列表
      getTaskList(true);
    }

    // 建立SSE连接
    let eventSource = new EventSource(
      `${API_SERVER_URL}/subscribe_messages?device_id=${teamInfo.session_uuid}`
    );
    // 监听消息
    const onMessage = (event) => {
      const message = JSON.parse(event.data);
      console.log("收到消息:", message);
      // 处理连接成功消息
      if (message.type === "connected") {
        console.log("SSE连接成功");
        return;
      } else if (message.type === "player_active_ready") {
        // 激活准备，展示九宫格禁用的能量站
        getTeamInfo();
      } else if (message.type === "game_start") {
        setGameHintType(1);
        setTimeout(() => getTeamInfo(true), 500);
      } else if (message.type === "game_pause") {
        setTimeout(() => getTeamInfo(true), 500);
      } else if (message.type === "game_resume") {
        setTimeout(() => getTeamInfo(true), 500);
      } else if (message.type === "player_actived") {
        if (message.team_uuid === getTeamUUID()) {
          setGameHintType(1);
          setShowGameStart(true);
          setTimeout(() => setShowGameStart(false), 2000);
          setTimeout(() => getTeamInfo(true), 500);
        }
      } else if (message.type === "game_end") {
        setGameHintType(2);
        setShowGameStart(true);
        setTimeout(() => setShowGameStart(false), 2000);
        setTimeout(() => getTeamInfo(true), 500);
      } else if (message.type === "photo_judge") {
        if (message.photo_type === 1) {
          // 刷新本人+本队
          if (message.team_uuid === getTeamUUID()) {
            getTeamInfo(true);
            taskTypeSelectedRef.current === 1 && getTaskList(true);
          }
        } else if (message.photo_type === 2) {
          // 刷新本人+本队+被抢队伍
          if (
            message.team_uuid === getTeamUUID() ||
            message.rob_team === getTeamUUID()
          ) {
            getTeamInfo(true);
            taskTypeSelectedRef.current === 2 && getRobList(true);
          }
        } else if (message.photo_type === 3) {
          if (teamInfo?.level === 3) {
            getTeamInfo(true);
            taskTypeSelectedRef.current === 3 && getHubList(true);
          }
        } else if (message.photo_type === 99) {
          if (!teamInfo?.actived) {
            // 如果未激活，刷新激活信息
            getTeamInfo(true);
            getActiveHubList(true);
          }
        } else if (message.photo_type === 11) {
          setHasSubmitSupply(false);
          setSupplyStatusText("");
          setHasTakeSupplyPhoto(false);
          getSessionSupply();
          getTeamInfo(true);
        }
      } else if (message.type === "team_upgrade") {
        if (message.team_uuid === getTeamUUID()) {
          Alert.show({
            type: "alert",
            title: "队伍升级",
            message: `等级升为${message.level}级，已解锁新的功能`,
          });
          setTimeout(() => getTeamInfo(true), 600);
        }
      } else if (message.type === "player_submit_rob") {
        // getTeamInfo();
        // taskTypeSelected === 2 && getRobList();
      } else if (message.type === "player_submit_occupy_energy_hub") {
        getTeamInfo(true);
        taskTypeSelectedRef.current === 3 && getHubList(true);
      } else if (message.type === "player_submit_active_energy_hub") {
        if (!teamInfo.actived) {
          getTeamInfo();
          getActiveHubList();
        }
      } else if (message.type === "show_team_rank") {
        getTeamInfo();
        getTeamRank();
      } else if (message.type === "start_guide") {
        navigate("/guide", { replace: true });
      } else if (message.type === "punish_team") {
        if (message.team_uuid === getTeamUUID()) {
          // 如果是本队伍或者被抢队伍，刷新信息
          setTimeout(() => getTeamInfo(true), 600);
          Alert.show({
            type: "alert",
            title: "犯规提醒",
            message: `经核实，你方成员存在犯规行为<br />已扣除团队${Math.abs(
              message.energy
            )}点能量值`,
          });
        }
      } else if (message.type === "adjust_team_energy") {
        if (message.team_uuid === getTeamUUID()) {
          setTimeout(() => getTeamInfo(true), 600);
        }
      } else if (
        message.type === "supply_update" ||
        message.type === "player_get_supply"
      ) {
        getSessionSupply();
      }
    };
    // 连接建立时的回调
    eventSource.onopen = () => {
      console.log("SSE连接已建立");
    };
    eventSource.onmessage = onMessage;
    // 错误处理
    eventSource.onerror = (error) => {
      console.error("SSE错误:", error);
      eventSource.close();

      // 3s后尝试重连
      setTimeout(() => {
        // Create a new EventSource connection
        eventSource = new EventSource(
          `${API_SERVER_URL}/subscribe_messages?device_id=${teamInfo.session_uuid}`
        );
        // Re-attach event handlers
        eventSource.onmessage = onMessage;
      }, 3000);
    };
    // 在组件卸载时关闭连接
    return () => {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    };
  }, [teamInfo?.session_uuid, teamInfo?.actived, teamInfo?.level]);

  const currentSupplyPhotoRef = useRef();
  const [hasTakeSupplyPhoto, setHasTakeSupplyPhoto] = useState(false);
  const [hasSubmitSupply, setHasSubmitSupply] = useState(false);

  const onCameraChange = (e) => {
    console.log("onCameraChange 被触发", e);
    if (!e.target.files || e.target.files.length === 0) {
      console.log("没有选择文件");
      return;
    }
    console.log("taskPhotoType: ", takePhotoType.current);
    // if (takePhotoType.current === "active") {
    //   setShowActiveSuccess(true);
    //   setTimeout(() => {
    //     // const loading = showLoading("加载中");
    //     apiRequest("/player_active", "POST", {
    //       tid: getTeamUUID(),
    //       code: getPlayerCode(),
    //     })
    //       .then((res) => {
    //         if (res.code === 0) {
    //           getTeamInfo();
    //         } else {
    //           showAlert(res.msg || "激活失败");
    //         }
    //       })
    //       .catch((err) => {
    //         console.error("激活请求出错:", err);
    //         showAlert("激活请求失败，请重试");
    //       });
    //     // .finally(() => loading.hide());
    //   }, 5500);
    // } else {
    const file = e.target.files[0];
    const loading = showLoading("上传中");
    // 先压缩图片
    compressImage(file)
      .then((compressedFile) => {
        // 上传到cos
        const cos = new COS(cosConfig.current);
        cos.uploadFile(
          {
            Bucket: "wanpilaoban-1314240291",
            Region: "ap-beijing",
            Key: `outdoor/photo/${getSessionUUID()}/${uuidv4()}.jpg`,
            Body: compressedFile,
            onTaskReady: function (taskId) {
              console.log("onTaskReady", taskId);
            },
            onProgress: function (progressData) {
              console.log(JSON.stringify(progressData));
            },
            onFileFinish: function (err, data, options) {
              console.log(options.Key + "上传" + (err ? "失败" : "完成"));
            },
          },
          function (err, data) {
            console.log(err || data);
            if (err) {
              showAlert("上传失败，请重试");
              return;
            } else {
              if (takePhotoType.current === "active") {
                apiRequest("/player_active_energy_hub", "POST", {
                  tid: getTeamUUID(),
                  code: getPlayerCode(),
                  energy_hub_id: currentHubRef.current.energy_hub_id,
                  photo: "https://" + data.Location,
                })
                  .then((res) => {
                    if (res.code === 0) {
                      getActiveHubList(true);
                    } else {
                      Alert.show({
                        type: "alert",
                        title: "激活失败",
                        message: res.msg,
                        onClose: () => window.location.reload(),
                      });
                    }
                  })
                  .finally(() => loading.hide());
              } else if (takePhotoType.current === "task") {
                apiRequest("/player_submit_task", "POST", {
                  tid: getTeamUUID(),
                  code: getPlayerCode(),
                  task_id: photoTaskId.current,
                  photo: "https://" + data.Location,
                })
                  .then((res) => {
                    if (res.code === 0) {
                      getTeamInfo();
                      setTimeout(() => getTaskList(true), 500);
                    } else {
                      Alert.show({
                        type: "alert",
                        title: "提交失败",
                        message: res.msg,
                        onClose: () => window.location.reload(),
                      });
                    }
                  })
                  .finally(() => loading.hide());
              } else if (takePhotoType.current === "rob") {
                apiRequest("/player_submit_rob", "POST", {
                  tid: getTeamUUID(),
                  code: getPlayerCode(),
                  rob_tid: robTeamUUID.current,
                  photo: "https://" + data.Location,
                })
                  .then((res) => {
                    if (res.code === 0) getRobList(true);
                    else {
                      Alert.show({
                        type: "alert",
                        title: "抢夺失败",
                        message: res.msg,
                        onClose: () => window.location.reload(),
                      });
                    }
                  })
                  .finally(() => loading.hide());
              } else if (takePhotoType.current === "hub") {
                apiRequest("/player_occupy_energy_hub", "POST", {
                  tid: getTeamUUID(),
                  code: getPlayerCode(),
                  energy_hub_id: currentHubRef.current.energy_hub_id,
                  photo: "https://" + data.Location,
                })
                  .then((res) => {
                    if (res.code === 0) getHubList(true);
                    else {
                      Alert.show({
                        type: "alert",
                        title: "提交失败",
                        message: res.msg,
                        onClose: () => window.location.reload(),
                      });
                    }
                    closeHubInfo();
                  })
                  .finally(() => loading.hide());
              } else if (takePhotoType.current === "supply") {
                // 这里不直接提交，弹出共享或者抢夺选项
                currentSupplyPhotoRef.current = "https://" + data.Location;
                setHasTakeSupplyPhoto(true);
                loading.hide();
                getSessionSupply();
              }
            }
          }
        );
      })
      .catch((error) => {
        console.error("图片压缩失败:", error);
        showAlert("图片处理失败，请重试");
        loading.hide();
      });
    // }
  };

  const [supplyStatusText, setSupplyStatusText] = useState("");
  const clickSupplyAction = (action) => {
    const loading = showLoading("加载中");
    apiRequest("/player_get_supply", "POST", {
      tid: getTeamUUID(),
      code: getPlayerCode(),
      supplyId: sessionSupply.id,
      photo: currentSupplyPhotoRef.current,
      action,
    })
      .then((res) => {
        if (res.code === 0) {
          setSupplyStatusText("");
          setHasSubmitSupply(true);
          closeSupplyModal();
          setTimeout(() => {
            setHasTakeSupplyPhoto(false);
          }, 800);
        } else {
          setHasSubmitSupply(true);
          setSupplyStatusText(res.msg);
        }
        setTimeout(() => getSessionSupply(), 800);
      })
      .finally(() => {
        loading.hide();
      });
  };

  useEffect(() => {
    // 补给展示依赖 teamInfo
    getSessionSupply();
    // 使用ref来访问相机输入元素
    if (!cameraInputRef.current) {
      console.log("相机输入元素尚未准备好，等待DOM渲染完成");
      return;
    }

    console.log("绑定相机事件监听器");
    const handleCameraChange = onCameraChange; // 创建一个引用以避免闭包问题

    // 移除可能存在的旧监听器
    cameraInputRef.current.removeEventListener("change", handleCameraChange);
    // 添加新的监听器
    cameraInputRef.current.addEventListener("change", handleCameraChange);

    return () => {
      console.log("移除相机事件监听器");
      if (cameraInputRef.current) {
        cameraInputRef.current.removeEventListener(
          "change",
          handleCameraChange
        );
      }
    };
  }, [teamInfo]); // 当teamInfo变化时重新绑定，确保在组件完全加载后绑定

  const openCamera = () => {
    if (cameraInputRef.current) {
      // 重置input的value，确保同一文件可以再次触发change事件
      cameraInputRef.current.value = "";
      cameraInputRef.current.click();
    } else {
      console.warn("未找到相机输入元素");
    }
  };

  const playerActive = () => {
    takePhotoType.current = "active";
    openCamera();
  };

  const takePhotoTask = (taskId) => {
    takePhotoType.current = "task";
    photoTaskId.current = taskId;
    openCamera();
  };

  // 发起队伍抢夺
  const makeRobTask = (teamUUID) => {
    takePhotoType.current = "rob";
    robTeamUUID.current = teamUUID;
    openCamera();
  };

  // 发起占领能量站
  const makeHubTask = () => {
    takePhotoType.current = "hub";
    openCamera();
  };

  const sessionSupplyIdRef = useRef(null);
  // 获取补给
  const takeSupplyPhoto = (sessionSupplyId) => {
    takePhotoType.current = "supply";
    sessionSupplyIdRef.current = sessionSupplyId;
    openCamera();
  };

  const startCountdownRef = useRef(null);
  // 任务开始倒计时
  useEffect(() => {
    if (!showActiveSuccess) return;
    startCountdownRef.current = setInterval(() => {
      setStartCountdownNum((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(startCountdownRef.current);
  }, [showActiveSuccess]);

  useEffect(() => {
    if (startCountdownNum <= 0) {
      clearInterval(startCountdownRef.current);
      setShowGameStart(true);
      setTimeout(() => {
        setShowGameStart(false);
      }, 2000);
      setTimeout(() => {
        setShowActiveSuccess(false);
      }, 1000);
    }
  }, [startCountdownNum]);

  const closeHubInfo = () => {
    setHubInfoClosing(true);
    setTimeout(() => {
      setShowHubInfo(false);
      setHubInfoClosing(false);
    }, 600);
  };

  const closeTeamCenter = () => {
    setTeamCenterClosing(true);
    setTimeout(() => {
      setShowTeamCenter(false);
      setTeamCenterClosing(false);
    }, 600);
  };

  const closeSupplyModal = () => {
    setSupplyModalClosing(true);
    setTimeout(() => {
      setShowSupplyModal(false);
      setSupplyModalClosing(false);
    }, 600);
  };

  if (!teamInfo) return null;

  return (
    <div className={ss.gameContainer}>
      <div className={ss.preload}>
        <img src={require("./imgs/supply_bg_hl.png")} alt="supplyBoxDown" />
      </div>
      {/* 拍照input - 添加ref */}
      <input
        ref={cameraInputRef}
        style={{ position: "absolute", visibility: "hidden" }}
        type="file"
        accept="image/*"
        capture="camera"
        id="cameraInput"
      />
      <div className={ss.teamCard}>
        <div className={ss.teamHeader}>
          <div className={ss.teamName}>
            <img
              className={ss.teamIcon}
              src={require(`../../imgs/team/color${teamInfo.color}.png`)}
              alt=""
              onClick={() => window.location.reload()}
            />
            <h2 className={ss.teamTitle}>{teamInfo.name}</h2>
            <div
              className={ss.playerNo}
              style={{ color: TEAM_COLORS[teamInfo.color] }}
            >
              {formatPlayerNo(teamInfo.player_no)}
            </div>
          </div>
          <div
            className={classNames(
              ss.teamCenterButton,
              teamInfo.game_status === 200 && ss.disabled
            )}
            onClick={() => {
              getTeamCenterInfo();
              setShowTeamCenter(true);
            }}
          >
            队伍中心
          </div>

          <div className={ss.teamInfo}>
            <div className={ss.infoItem}>
              <div className={ss.infoLabel}>顽皮能量值</div>
              <div className={ss.infoValue}>
                {teamInfo.game_status === 200 ? "????" : teamInfo.energy}
              </div>
            </div>
            <div className={ss.infoItem}>
              <div className={ss.infoLabel}>当前等级</div>
              <div className={ss.stars}>
                <span className={ss.star}>
                  {teamInfo.level >= 1 && teamInfo.actived ? "★" : "☆"}
                </span>
                <span className={ss.star}>
                  {teamInfo.level >= 2 && teamInfo.actived ? "★" : "☆"}
                </span>
                <span className={ss.star}>
                  {teamInfo.level >= 3 && teamInfo.actived ? "★" : "☆"}
                </span>
              </div>
            </div>
            <div className={ss.infoItem}>
              <div className={ss.infoLabel}>剩余时间</div>
              <div className={ss.infoValue}>
                {teamInfo.game_status === 0 && "??"}
                {teamInfo.game_status === 50 && "0"}
                {teamInfo.game_status >= 100 &&
                  teamInfo.game_status < 200 &&
                  Math.ceil(remainTimeCountdown / 60)}
                {teamInfo.game_status >= 200 && "0"}
              </div>
            </div>
          </div>
        </div>

        <div
          className={classNames(ss.contentBox, showActiveSuccess && ss.active)}
        >
          {(!teamInfo.actived ||
            showActiveSuccess ||
            teamInfo.game_status === 200) && (
            <>
              {!showActiveSuccess &&
                ((!teamInfo.actived && teamInfo.game_status < 50) ||
                  teamInfo.game_status === 200) && (
                  <div className={ss.map}>
                    <img
                      src={
                        teamInfo.game_status === 200
                          ? sessionMap.map_photo
                          : sessionMap.inactive_photo
                      }
                    />
                  </div>
                )}
              {showActiveSuccess && (
                <div className={ss.mapPreview}>
                  <div>
                    <div>激活成功</div>
                    <div className={ss.startHint}>&lt; 任务即将开启 &gt;</div>
                    <div className={ss.startCountdown}>{startCountdownNum}</div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* 激活任务能量站 */}
          {!teamInfo.actived &&
            teamInfo.game_status > 0 &&
            teamInfo.game_status < 200 && (
              <>
                <div className={ss.hubList}>
                  {activeHubList.map((hub) => {
                    // 没有被任何队伍使用过：可点击状态
                    // 已经被别的队伍提交照片：失效状态
                    // 已经被别的队伍激活成功：失效状态
                    // 本队伍已经提交照片：正在判定状态+全部置灰
                    // 自己提交后，判定失败：全部恢复可点击（已被别的队伍激活成功的保持不变）
                    // 别的队伍提交后，判定失败：恢复可点击
                    // 已被使用
                    const isUsed = activeHubJudgingList.find(
                      (item) =>
                        item.energy_hub_id === hub.energy_hub_id &&
                        item.team_uuid !== getTeamUUID() &&
                        item.status === 2
                    );
                    // 别的队伍判定中
                    const isOtherJudging = activeHubJudgingList.find(
                      (item) =>
                        item.energy_hub_id === hub.energy_hub_id &&
                        item.team_uuid !== getTeamUUID() &&
                        item.status === 1
                    );
                    // 自己判定的能两张
                    const isSelfJudging = activeHubJudgingList.find(
                      (item) =>
                        item.energy_hub_id === hub.energy_hub_id &&
                        item.team_uuid === getTeamUUID() &&
                        item.status === 1
                    );
                    // 在判定中
                    const isJudging = activeHubJudgingList.find(
                      (item) =>
                        item.energy_hub_id !== hub.energy_hub_id &&
                        item.team_uuid === getTeamUUID() &&
                        item.status === 1
                    );
                    return (
                      <div
                        className={classNames(
                          ss.hubItem,
                          (teamInfo.game_status === 50 ||
                            isUsed ||
                            isSelfJudging ||
                            isJudging ||
                            isOtherJudging) &&
                            ss.cd
                        )}
                        onClick={() => {
                          currentHubRef.current = hub;
                          playerActive();
                        }}
                      >
                        <>
                          <div className={ss.hubItemName}>
                            {hub.energy_hub_name}
                          </div>
                          {(isUsed || isOtherJudging) && (
                            <div className={ss.hubItemOccupied}>已失效</div>
                          )}
                          {isSelfJudging && (
                            <div className={ss.hubItemOccupied}>正在判定中</div>
                          )}
                        </>
                      </div>
                    );
                  })}
                </div>
                <div className={ss.hubLogList}>
                  {activeHubLogList.map((log) => {
                    return (
                      <div className={ss.hubLogItem}>
                        <div className={ss.hubLogItemLabel}>{log.desc}</div>
                        <div
                          className={classNames(
                            ss.hubLogItemValue,
                            log.type === 1 && ss.robbed
                          )}
                        >
                          {log.value}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

          {teamInfo.actived &&
            !showActiveSuccess &&
            teamInfo.game_status >= 100 &&
            teamInfo.game_status < 200 && (
              <>
                {taskTypeSelected === 1 && (
                  <div className={ss.taskList}>
                    {taskList.map((task) => (
                      <div
                        className={classNames(
                          ss.taskCard,
                          task.status > 0 && ss.disabled
                        )}
                        key={task.id}
                      >
                        <div className={ss.label}>任务说明：</div>
                        <div className={ss.desc}>{task.desc}</div>
                        <div className={ss.taskFooter}>
                          {task.status === 1 && (
                            <div className={ss.taskFooterTxt}>
                              任务已提交，正在判定中…
                            </div>
                          )}
                          {task.status === 2 && (
                            <div className={ss.taskFooterTxt}>
                              任务成功 +{task.reward}
                            </div>
                          )}
                          {task.status === 3 && (
                            <div
                              className={classNames(
                                ss.taskFooterTxt,
                                ss.failed
                              )}
                            >
                              <span>判定失败：{task.failed_reason_desc}</span>
                            </div>
                          )}
                          {task.status === 0 && (
                            <>
                              <div
                                className={classNames(
                                  ss.changeBtn,
                                  !allowChangeTask && ss.disabled
                                )}
                                onClick={() => {
                                  Alert.show({
                                    type: "confirm",
                                    title: "更换任务",
                                    message: "请确认是否更换本项任务？",
                                    hint: "（更换后，1分钟内将不能再次更换）",
                                    onConfirm: () => {
                                      const loading = showLoading("加载中");
                                      apiRequest(
                                        "/player_change_task",
                                        "POST",
                                        {
                                          tid: getTeamUUID(),
                                          code: getPlayerCode(),
                                        }
                                      )
                                        .then((res) => {
                                          if (res.code === 0) {
                                            setAllowChangeTask(false);
                                            getTaskList();
                                            setTimeout(
                                              () => setAllowChangeTask(true),
                                              120 * 1000
                                            );
                                          } else showAlert(res.msg);
                                        })
                                        .finally(() => loading.hide());
                                    },
                                  });
                                }}
                              >
                                更换
                              </div>
                              <div className={ss.actionBtn}>
                                <div
                                  className={ss.actionBtnText}
                                  onClick={() => takePhotoTask(task.task_id)}
                                >
                                  完成任务
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <div
                          className={classNames(
                            ss.reward,
                            task.status > 0 && ss.gray
                          )}
                        >
                          奖励：+{task.reward}点
                        </div>
                        <div
                          className={classNames(
                            ss.position,
                            task.status > 0 && ss.gray
                          )}
                        >
                          位置：{task.position}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {taskTypeSelected === 2 && (
                  <>
                    <div className={ss.robList}>
                      {robList.map((rob) => (
                        <div
                          className={classNames(
                            ss.robItem,
                            robCooldownMap[rob.uuid] > 0 && ss.cd
                          )}
                          key={rob.uuid}
                          style={{
                            backgroundImage: `url(${
                              robCooldownMap[rob.uuid] > 0
                                ? require(`./imgs/rob_team_bg_gray.png`)
                                : require(`../../imgs/team/bg${rob.color}.png`)
                            })`,
                            backgroundSize: "100% 100%",
                            backgroundRepeat: "no-repeat",
                            color:
                              robCooldownMap[rob.uuid] > 0
                                ? "#a5a5a5"
                                : TEAM_COLORS[rob.color],
                          }}
                          onClick={() => makeRobTask(rob.uuid)}
                        >
                          <div
                            className={ss.robItemIcon}
                            style={{
                              backgroundImage: `url(${
                                robCooldownMap[rob.uuid] > 0
                                  ? require(`../../imgs/team_color_gray.png`)
                                  : require(`../../imgs/team/color${rob.color}.png`)
                              })`,
                              backgroundSize: "100% 100%",
                              backgroundRepeat: "no-repeat",
                            }}
                          ></div>
                          <div className={ss.robItemName}>{rob.name}</div>
                          {robCooldownMap[rob.uuid] > 0 && (
                            <div className={ss.robItemCountdown}>
                              {robCooldownMap[rob.uuid]}
                              <span>s</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className={ss.robLogList}>
                      {robLogList.map((robLog) => (
                        <div
                          key={robLog.id}
                          className={classNames(
                            ss.robLogItem,
                            robLog.rob_team_uuid !== getTeamUUID() &&
                              robLog.status === 1 &&
                              ss.pending
                          )}
                        >
                          {robLog.rob_team_uuid === getTeamUUID() &&
                            robLog.status === 2 && (
                              <>
                                <div className={ss.robLogItemLabel}>被抢夺</div>
                                <div
                                  className={classNames(
                                    ss.robLogItemValue,
                                    ss.robbed
                                  )}
                                >
                                  -{robLog.rob_energy}
                                </div>
                              </>
                            )}
                          {robLog.rob_team_uuid !== getTeamUUID() && (
                            <>
                              {robLog.status === 1 && (
                                <>
                                  <div
                                    className={classNames(
                                      ss.robLogItemLabel,
                                      ss.pending
                                    )}
                                  >
                                    抢夺【{robLog.rob_team_name}】已提交
                                  </div>
                                  <div
                                    className={classNames(
                                      ss.robLogItemValue,
                                      ss.pending
                                    )}
                                  >
                                    正在判定中
                                  </div>
                                </>
                              )}
                              {robLog.status === 2 && (
                                <>
                                  <div className={ss.robLogItemLabel}>
                                    抢夺【{robLog.rob_team_name}】成功
                                  </div>
                                  <div
                                    className={classNames(
                                      ss.robLogItemValue,
                                      ss.success
                                    )}
                                  >
                                    +{robLog.rob_energy}
                                  </div>
                                </>
                              )}
                              {robLog.status === 3 && (
                                <>
                                  <div className={ss.robLogItemLabel}>
                                    抢夺【{robLog.rob_team_name}】失败
                                  </div>
                                  <div
                                    className={classNames(
                                      ss.robLogItemValue,
                                      ss.failed
                                    )}
                                  >
                                    {robLog.failed_reason_desc}
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {taskTypeSelected === 3 && (
                  <>
                    <div className={ss.hubList}>
                      {hubList.map((hub) => {
                        // 自己判定中的能量站
                        const isSelfJudging = hubJudgingList.find(
                          (item) =>
                            item.energy_hub_id === hub.energy_hub_id &&
                            item.team_uuid === getTeamUUID()
                        );
                        // 其他队伍判定中的能量站
                        const isOtherJudging = hubJudgingList.find(
                          (item) =>
                            item.energy_hub_id === hub.energy_hub_id &&
                            item.team_uuid !== getTeamUUID()
                        );
                        // 自己已占领的能量站
                        const isSelfOccupied = hubList.find(
                          (item) =>
                            item.energy_hub_id === hub.energy_hub_id &&
                            item.current_team_uuid === getTeamUUID()
                        );
                        // 其他队伍已占领的能量站
                        const isOtherOccupied = hubList.find(
                          (item) =>
                            item.energy_hub_id === hub.energy_hub_id &&
                            item.current_team_uuid &&
                            item.current_team_uuid !== getTeamUUID()
                        );
                        const isDisabled = hub.status === 0;
                        return (
                          <div
                            className={classNames(
                              ss.hubItem,
                              (isSelfJudging || isOtherJudging) && ss.cd,
                              isDisabled && ss.disabled
                            )}
                            onClick={() => {
                              currentHubRef.current = hub;
                              setShowHubInfo(true);
                            }}
                          >
                            {!isOtherJudging && !isSelfOccupied && (
                              <>
                                <div className={ss.hubItemName}>
                                  {hub.energy_hub_name}
                                </div>
                                {/* 未占领 */}
                                {!hub.current_team_id && (
                                  <div className={ss.hubItemValue}>
                                    【+{hub.energy_rate}点】
                                  </div>
                                )}
                              </>
                            )}

                            {/* 已被本队占领 */}
                            {isSelfOccupied && !isOtherJudging && (
                              <>
                                <div
                                  className={ss.flag}
                                  style={{
                                    backgroundImage: `url(${require(`../../imgs/team/flag${teamInfo.color}.png`)})`,
                                  }}
                                />
                                <div className={ss.hubItemName}>
                                  {hub.energy_hub_name}
                                </div>
                                <div className={ss.hubItemValue}>
                                  【+{hub.energy_rate}点】
                                </div>
                                {/* <div className={ss.hubItemOccupied}>已占领</div> */}
                              </>
                            )}
                            {/* 已被其他队伍占领并且不在判定中 */}
                            {isOtherOccupied && !isOtherJudging && (
                              <>
                                <div
                                  className={ss.flag}
                                  style={{
                                    backgroundImage: `url(${require(`../../imgs/team/flag${hub.current_team_color}.png`)})`,
                                  }}
                                ></div>
                                <div className={ss.hubItemValue}>
                                  【+{hub.energy_rate}点】
                                </div>
                              </>
                            )}
                            {/* 自己判定中的能量站 */}
                            {isSelfJudging && (
                              <div className={ss.hubItemOccupied}>
                                正在判定中
                              </div>
                            )}
                            {/* 其他队伍判定中的能量站，且不是本队已占领 */}
                            {isOtherJudging && (
                              <>
                                <div
                                  className={ss.flag}
                                  style={
                                    hub.current_team_color
                                      ? {
                                          backgroundImage: `url(${require(`../../imgs/team/flag${hub.current_team_color}.png`)})`,
                                        }
                                      : {}
                                  }
                                ></div>
                                <div className={ss.hubItemFrozen}>冻结中</div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className={ss.hubLogList}>
                      {hubLogList.map((log) => {
                        return (
                          <div className={ss.hubLogItem}>
                            <div className={ss.hubLogItemLabel}>{log.desc}</div>
                            <div
                              className={classNames(
                                ss.hubLogItemValue,
                                log.type === 2 && ss.success,
                                log.type === 3 && ss.robbed
                              )}
                            >
                              {log.value}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}

          {teamInfo.game_status >= 300 && teamRank.length > 0 && (
            <div className={ss.teamRank}>
              <div
                className={ss.wings}
                style={{
                  backgroundImage: `url(https://wanpilaoban-1314240291.cos.ap-beijing.myqcloud.com/outdoor/static/imgs/wings/wings${teamRank[0].color}.png`,
                }}
              >
                <div
                  className={ss.flag}
                  style={{
                    backgroundImage: `url(${require(`../../imgs/team/flag${teamRank[0].color}.png`)})`,
                  }}
                ></div>
              </div>
              <div className={ss.rankBanner}></div>
              <div className={ss.rankList}>
                {teamRank.map((item, index) => (
                  <div
                    className={classNames(ss.rankItem, index < 3 && ss.hl)}
                    key={item.id}
                  >
                    <div
                      className={ss.rankNum}
                      style={{
                        backgroundImage: `url(${
                          index < 3
                            ? require(`./imgs/rank_num_${index + 1}.png`)
                            : require(`./imgs/rank_num_bg.png`)
                        })`,
                      }}
                    >
                      {index < 3 ? "" : index + 1}
                    </div>
                    <div
                      className={ss.rankIcon}
                      style={{
                        backgroundImage: `url(${require(`../../imgs/team/color${item.color}.png`)})`,
                      }}
                    ></div>
                    <div
                      className={ss.rankName}
                      style={{
                        color: TEAM_COLORS[item.color],
                      }}
                    >
                      {item.name}
                    </div>
                    <div className={ss.rankEnergy}>{item.energy}/点</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={ss.bottomShadow}></div>
      <div
        className={classNames(
          ss.teamFooter,
          teamInfo.game_status >= 300 && ss.hide
        )}
      >
        {/* 激活流程修改 */}
        {/* {!teamInfo.actived && teamInfo.game_status < 200 && (
          <div
            className={classNames(
              ss.activeBtn,
              (teamInfo.game_status <= 0 || showActiveSuccess) && ss.disabled
            )}
            onClick={playerActive}
          >
            激活
          </div>
        )} */}
        {/* 激活准备状态 */}
        {!teamInfo.actived &&
          teamInfo.game_status !== 0 &&
          teamInfo.game_status !== 200 &&
          !activeHubJudgingList.find(
            (item) => item.team_uuid === getTeamUUID() && item.status === 1
          ) && (
            <div className={ss.bottomText}>请尽快前往赛场并完成激活任务</div>
          )}
        {/* 照片已提交，正在判定中 */}
        {!teamInfo.actived &&
          activeHubJudgingList.find(
            (item) => item.team_uuid === getTeamUUID() && item.status === 1
          ) && <div className={ss.bottomText}>照片已提交成功，正在判定中</div>}
        {/* 游戏结束 */}
        {teamInfo.game_status === 200 && (
          <div className={ss.bottomText}>
            请尽快返回&lt;顽皮老板&gt;店铺
            <br />
            最终胜负即将揭晓
          </div>
        )}
        {((!teamInfo.actived && teamInfo.game_status === 0) ||
          (teamInfo.actived && teamInfo.game_status < 200)) && (
          <>
            <div
              className={classNames(
                ss.taskBtn,
                taskTypeSelected === 1 && ss.selected,
                (!teamInfo.actived ||
                  teamInfo.level < 1 ||
                  teamInfo.game_status === 200) &&
                  ss.lock
              )}
              onClick={() => {
                setTaskTypeSelected(1);
                replaceUrlParams("t", 1);
              }}
            >
              任务
            </div>
            <div
              className={classNames(
                ss.taskBtn,
                taskTypeSelected === 2 && ss.selected,
                (!teamInfo.actived ||
                  teamInfo.level < 2 ||
                  teamInfo.game_status === 200) &&
                  teamInfo.upgrade_progress &&
                  ss.progress
              )}
              onClick={() => {
                setTaskTypeSelected(2);
                replaceUrlParams("t", 2);
              }}
            >
              {teamInfo.upgrade_progress && teamInfo.level === 1
                ? teamInfo.upgrade_progress
                : "抢夺"}
            </div>
            <div
              className={classNames(
                ss.taskBtn,
                taskTypeSelected === 3 && ss.selected,
                (!teamInfo.actived ||
                  teamInfo.level < 3 ||
                  teamInfo.game_status === 200) &&
                  ss.lock,
                teamInfo.upgrade_progress && teamInfo.level === 2 && ss.progress
              )}
              onClick={() => {
                setTaskTypeSelected(3);
                replaceUrlParams("t", 3);
              }}
            >
              {teamInfo.upgrade_progress && teamInfo.level === 2
                ? teamInfo.upgrade_progress
                : "能量站"}
            </div>
          </>
        )}
      </div>
      {/* 游戏开始弹层 */}
      <div className={classNames(ss.mask, showGameStart && ss.show)}>
        <div
          className={classNames(
            ss.gameStart,
            gameHintType === 2 && ss.gameEnd,
            showGameStart && ss.show
          )}
        ></div>
      </div>
      {/* 能量站弹层 */}
      {showHubInfo && (
        <div className={classNames(ss.hubMask, hubInfoClosing && ss.fadeOut)}>
          <div
            className={classNames(ss.hubModal, hubInfoClosing && ss.zoomOut)}
          >
            <div className={ss.hubInfo}>
              {currentHubRef.current.current_team_color && (
                <div
                  className={ss.flag}
                  style={{
                    backgroundImage: `url(${require(`../../imgs/team/flag${currentHubRef.current.current_team_color}.png`)})`,
                  }}
                />
              )}
              <div className={ss.closeBtn} onClick={closeHubInfo}></div>
              <div className={ss.hubName}>
                <span>{currentHubRef.current.energy_hub_name}</span>
              </div>
              <div className={ss.photoHint}>
                &lt; 请根据指定人数及动作进行合照 &gt;
              </div>
              <div className={ss.photoInfo}>
                <div className={ss.photoInfoItem}>
                  动作: {currentHubRef.current.photo_guide}
                </div>
                <div className={ss.photoInfoItem}>
                  人数:{" "}
                  {getEnergyHubPeopleNumText(currentHubRef.current.people_num)}
                </div>
              </div>
              <div className={ss.photoInfoFields}>
                <div className={ss.photoInfoField}>
                  站点位置：{currentHubRef.current.position}
                </div>
                <div className={ss.photoInfoField}>
                  占领奖励：+{currentHubRef.current.energy_rate}点
                </div>
              </div>
              <div
                className={classNames(
                  ss.hubBtn,
                  currentHubRef.current.status === 0 && ss.disabled
                )}
                onClick={makeHubTask}
              >
                占领能量站
              </div>
            </div>
          </div>
        </div>
      )}
      {/* 队伍中心弹层 */}
      {showTeamCenter && (
        <div
          className={classNames(ss.hubMask, teamCenterClosing && ss.fadeOut)}
        >
          <div
            className={classNames(
              ss.teamCenter,
              teamCenterClosing && ss.zoomOut
            )}
          >
            <div className={ss.teamCenterClose} onClick={closeTeamCenter}></div>
            <div className={ss.teamCenterTitle}>队伍中心</div>
            <div className={ss.teamCenterInfo}>
              <div className={ss.teamCenterInfoItem}>
                <div className={ss.teamCenterInfoLable}>任务成功</div>
                <div className={ss.teamCenterInfoValue}>
                  {teamCenterInfo.tasks_success}/次
                </div>
              </div>
              <div className={ss.teamCenterInfoItem}>
                <div className={ss.teamCenterInfoLable}>抢夺成功</div>
                <div className={ss.teamCenterInfoValue}>
                  {teamCenterInfo.rob_success}/次
                </div>
              </div>
              <div className={ss.teamCenterInfoItem}>
                <div className={ss.teamCenterInfoLable}>被抢夺</div>
                <div className={ss.teamCenterInfoValue}>
                  {teamCenterInfo.be_robbed_success}/次
                </div>
              </div>
              {/*<div className={ss.teamCenterInfoItem}>
                <div className={ss.teamCenterInfoLable}>占领成功</div>
                <div className={ss.teamCenterInfoValue}>
                  {teamCenterInfo.occupy_success}/次
                </div>
              </div>*/}
              <div className={ss.teamCenterInfoItem}>
                <div className={ss.teamCenterInfoLable}>能量站收益</div>
                <div className={ss.teamCenterInfoValue}>
                  {teamCenterInfo.occupy_total_reward}/点
                </div>
              </div>
              <div className={ss.teamCenterInfoItem}>
                <div className={ss.teamCenterInfoLable}>补给收益</div>
                <div className={ss.teamCenterInfoValue}>
                  {teamCenterInfo.supply_total_reward}/点
                </div>
              </div>
              <div className={ss.teamCenterInfoItem}>
                <div className={ss.teamCenterInfoLable}>判定失败</div>
                <div className={classNames(ss.teamCenterInfoValue, ss.failed)}>
                  {teamCenterInfo.failed_energy}/点
                </div>
              </div>
            </div>
            <div className={ss.teamCenterMap}>
              <img src={sessionMap.tc_map} />
            </div>
          </div>
        </div>
      )}
      <div
        className={classNames(
          ss.supplyBox,
          showSessionSupply && ss.show,
          sessionSupply?.status !== 0 && ss.gray,
          sessionSupply?.status === 1 && ss.disabled
        )}
        onClick={() => {
          setHasTakeSupplyPhoto(false);
          if (sessionSupply.status === 0) {
            setShowSupplyModal(true);
          } else if (sessionSupply.status === 2) {
            localStorage.setItem("hideSessionSupply", sessionSupply.id);
            setShowSessionSupply(false);
            setTimeout(() => setSessionSupply(null), 800);
          }
        }}
      >
        {sessionSupply?.status === 0 && (
          <div className={ss.supplyText}>{sessionSupply.type}</div>
        )}
        {sessionSupply?.status === 1 &&
          sessionSupply.photo_team_uuid === getTeamUUID() && (
            <div className={classNames(ss.supplyText, ss.gray)}>正在判定中</div>
          )}
        {sessionSupply?.status === 1 &&
          sessionSupply.photo_team_uuid !== getTeamUUID() && (
            <div className={classNames(ss.supplyText, ss.gray)}>已冻结</div>
          )}
        {sessionSupply?.status >= 2 && (
          <>
            <img
              className={classNames(ss.supplyWinner)}
              src={require(`../../imgs/team/color${sessionSupply.photo_team_color}.png`)}
              alt=""
            />
            <div
              className={classNames(ss.supplyText)}
              style={{ color: TEAM_COLORS[sessionSupply.photo_team_color] }}
            >
              完成队伍
            </div>
          </>
        )}
      </div>
      {/* 补给弹层 */}
      {showSupplyModal && (
        <div
          className={classNames(ss.hubMask, supplyModalClosing && ss.fadeOut)}
        >
          <div
            className={classNames(
              ss.supplyModal,
              supplyModalClosing && ss.zoomOut
            )}
          >
            <div className={ss.supplyInfo}>
              <div
                className={ss.supplyClose}
                onClick={() => {
                  closeSupplyModal();
                  setHasTakeSupplyPhoto(false);
                }}
              ></div>
              <div className={ss.supplyType}>
                <span>{sessionSupply.type}</span>
              </div>
              {hasTakeSupplyPhoto ? (
                <>
                  <div className={ss.supplyPhotoBox}>
                    <img src={currentSupplyPhotoRef.current} />
                  </div>
                  <div className={ss.supplySelectHint}>
                    <span>&lt; 请选择共享或抢夺 &gt;</span>
                  </div>
                  <div className={ss.supplyPhotoActionBtns}>
                    <div
                      className={classNames(
                        ss.supplyPhotoActionBtn,
                        hasSubmitSupply && ss.disabled
                      )}
                      onClick={() => clickSupplyAction(1)}
                    >
                      共享
                    </div>
                    <div
                      className={classNames(
                        ss.supplyPhotoActionBtn,
                        hasSubmitSupply && ss.disabled
                      )}
                      onClick={() => clickSupplyAction(2)}
                    >
                      抢夺
                    </div>
                  </div>
                  <div className={ss.failedReason}>{supplyStatusText}</div>
                </>
              ) : (
                <>
                  <div className={ss.supplyHint}>
                    &lt; 率先完成的队伍可获得奖励 &gt;
                  </div>
                  <div className={ss.actionTip}>
                    <p>&lt; 共享：双方各得400点 &gt;</p>
                    <p>&lt; 抢夺：抢走对方800点 &gt;</p>
                  </div>
                  <div className={ss.supplyItemRow}>
                    <div className={ss.supplyItemRowLabel}>对象</div>
                    <div
                      className={ss.supplyItemRowTeamFlag}
                      style={{
                        backgroundImage: `url(${require(`../../imgs/team/color${sessionSupply.target_team_color}.png`)})`,
                      }}
                    />
                    <div
                      className={ss.supplyItemRowTeamName}
                      style={{
                        color: TEAM_COLORS[sessionSupply.target_team_color],
                      }}
                    >
                      {sessionSupply.target_team_name}
                    </div>
                  </div>
                  <div className={ss.supplyItemRow}>
                    <div className={ss.supplyItemRowLabel}>任务</div>
                    <span>{sessionSupply.desc}</span>
                  </div>
                  <div className={ss.supplyItemRow}>
                    <div className={ss.supplyItemRowLabel}>奖励</div>
                    <span>{sessionSupply.reward}</span>
                  </div>
                  <div className={ss.supplySelectHint}>
                    <span>&lt; 合影后可选择共享或抢夺 &gt;</span>
                  </div>
                  <div
                    className={classNames(
                      ss.getSupplyBtn,
                      // 当自己是目标队伍时，不能
                      sessionSupply.target_team_uuid === getTeamUUID() &&
                        ss.disabled,
                      // 当补给锁定或者已被领取
                      sessionSupply.status > 0 && ss.disabled
                    )}
                    onClick={() => takeSupplyPhoto(sessionSupply.id)}
                  >
                    完成任务
                  </div>
                  {sessionSupply.target_team_uuid === getTeamUUID() && (
                    <div className={ss.selectTeamDesc}>
                      <span>你方队伍已被选中</span>
                    </div>
                  )}
                  {sessionSupply.status === 0 &&
                    sessionSupply.failed_resaon && (
                      <div className={ss.failedReason}>
                        <span>判定失败：{sessionSupply.failed_resaon}</span>
                      </div>
                    )}
                  {/* 当补给锁定或者已被领取 */}
                  {sessionSupply.target_team_uuid !== getTeamUUID() &&
                    sessionSupply.status > 0 && (
                      <div className={ss.failedReason}>
                        <span>
                          {sessionSupply.photo_team_uuid === getTeamUUID()
                            ? "队友已提交照片"
                            : "已有其他队伍提交照片"}
                        </span>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
